// libs
import * as React from "react";
import {
  Link,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  InputBase,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import moment from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import LoadingReportsTable from "../shared/LoadingReportsTable";
import EmptyReportsTable from "../shared/EmptyReportsTable";

interface Props {}

const QuestionnaireReport: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  const isInitialMount = React.useRef(true);
  const [questionnaireOptions, setQuestionnaireOptions] = React.useState<any[]>(
    []
  );
  const [startDate, setStartDate] = React.useState<string>(null);
  const [endDate, setEndDate] = React.useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [lastName, setLastName] = React.useState<string>("");
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] =
    React.useState<string>("");
  const [questionColumns, setQuestionColumns] = React.useState<any[]>([]);

  const [tableData, setTableData] = React.useState<any>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [pageLinks, setPageLinks] = React.useState<any>([]);
  const [paginatedList, setPaginatedList] = React.useState<any>([]);
  const perPage = 20;

  React.useEffect(() => {
    getAssets();
  }, []);

  React.useEffect(() => {
    getData();
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [startDate, endDate, selectedQuestionnaireId]);

  React.useEffect(() => {
    if (lastName.length > 1) {
      getData();
    } else if (lastName.length <= 1) {
      getData();
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [lastName]);

  const getAssets = () => {
    fetch(`/admin/reports/questionnaire/assets`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
        } else {
          setQuestionnaireOptions(result?.resource?.questionnaires || []);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  console.log("questionnaireOptions", questionnaireOptions)

  const getData = () => {
    if (!isInitialMount.current) {
      setLoading(true);
    }

    fetch(
      `/admin/reports/questionnaire/report_data?` +
        new URLSearchParams({
          start_date: startDate || "",
          end_date: endDate || "",
          last_name: lastName.length > 2 ? lastName : "",
          questionnaire_id: selectedQuestionnaireId || "",
        }),
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setTableData(result?.resource?.results || []);
          setQuestionColumns(result?.resource?.question_columns || []);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch report data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const paginateList = (list) => {
    if (list.length === 0) {
      return [];
    } else if (currentPage === 1) {
      return list.slice(0, perPage);
    } else {
      const offsetIndex = perPage * (currentPage - 1);
      return list.slice(offsetIndex, offsetIndex + perPage);
    }
  };

  const updatePagination = () => {
    setPaginatedList(paginateList(tableData));
  };

  const selectPage = (page) => {
    if (page < 1 || page > pageCount) return;
    setCurrentPage(page);
  };

  React.useEffect(() => {
    if (tableData) {
      const pages = tableData.length / perPage;
      setPageCount(Math.ceil(pages));

      const links = [];
      for (let i = 1; i <= Math.ceil(pages); i++) {
        links.push(
          <Link
            key={i}
            onClick={() => selectPage(i)}
            className={currentPage === i ? "linkDark" : "linkLight"}
          >
            {i}
          </Link>
        );
      }
      setPageLinks(links);
      updatePagination();
    }
  }, [tableData, currentPage]);

  return (
    <Box className="report-main-content">
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
              href={`/reports`}
            >
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px",
                  padding: "8px",
                  color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontSize: "14px",
                }}
              />
            </Link>
            <Typography className="main-heading">
              Questionnaire Report
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box className="report-details">
        <Box className="sub-header">
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Report Details</Typography>
            <Box className="count-pill">{tableData.length}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>
            <DatePickerWithLabel
              date={startDate}
              setDate={setStartDate}
              initialDate={moment().subtract(1, "month").format("YYYY-MM-DD")}
              label="Completion Start Date"
            />
            <DatePickerWithLabel
              date={endDate}
              setDate={setEndDate}
              initialDate={moment().format("YYYY-MM-DD")}
              label="Completion End Date"
            />
            <div className="date-container">
              <FormControl sx={{ width: "200px" }}>
                <Select
                  value={selectedQuestionnaireId}
                  onChange={(e) => {
                    setSelectedQuestionnaireId(e.target.value);
                    setCurrentPage(1);
                  }}
                  displayEmpty
                  sx={{
                    backgroundColor: "rgb(239, 233, 231)",
                    height: "42.5px",
                    margin: 0,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      fontSize: 12,
                      color: "#868382",
                      fontWeight: 500,
                      padding: "10px 14px",
                    },
                  }}
                >
                  <MenuItem value="">
                    Select Questionnaire
                  </MenuItem>
                  {questionnaireOptions.map((questionnaire) => (
                    <MenuItem key={questionnaire.id} value={questionnaire.id}>
                      {questionnaire.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <Button
              className="export-csv-button"
              onClick={() => {
                const params = new URLSearchParams({
                  start_date: startDate || "",
                  end_date: endDate || "",
                  last_name: lastName || "",
                  questionnaire_id: selectedQuestionnaireId || "",
                }).toString();

                window.location.href = `/admin/reports/questionnaire/generate_csv.csv?${params}`;
              }}
              disabled={tableData.length === 0}
            >
              Export as CSV
            </Button>
          </Box>
        </Box>

        <Box className="table-section">
          {loading ? (
            <LoadingReportsTable isBadgeShowing={false} />
          ) : (
            <>
              {paginatedList?.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{ height: "53.8vh" }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow
                        sx={{
                          "& td, & th": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell align="left" sx={{ minWidth: "170px" }}>
                          Organization Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>
                          Organization NPI
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization City
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization State
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization Zip
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "146px" }}>
                          Patient First Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "146px" }}>
                          Patient Last Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "125px" }}>
                          Patient Middle Initial
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient DOB
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "190px" }}>
                          Questionnaire Completion Date
                        </TableCell>
                        {questionColumns.map((question, index) => (
                          <TableCell
                            key={index}
                            align="left"
                            sx={{ minWidth: "200px" }}
                          >
                            {question.title}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedList?.map((report, index) => (
                        <TableRow
                          sx={{ "& td, & th": { borderBottom: "none" } }}
                          key={index + 1}
                        >
                          <TableCell align="left">
                            {report.customer_name || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_npi || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_city || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_state || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_zip || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {report.first_name}
                          </TableCell>
                          <TableCell align="left">{report.last_name}</TableCell>
                          <TableCell align="left">
                            {report.middle_name || ""}
                          </TableCell>
                          <TableCell align="left">{report.dob}</TableCell>
                          <TableCell align="left">
                            {report.completion_date}
                          </TableCell>
                          {questionColumns.map((question, qIndex) => (
                            <TableCell key={qIndex} align="left">
                              {report[`question_${question.id}`] || ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyReportsTable isBadgeShowing={false} />
              )}
            </>
          )}

          {paginatedList.length > 0 && (
            <p className="pagerLinks" style={{ marginBlockEnd: "0px" }}>
              <Link
                onClick={() => {
                  selectPage(currentPage > 1 ? currentPage - 1 : currentPage);
                }}
                className="linkDark"
              >
                &lt;
              </Link>
              {pageLinks}
              <Link
                onClick={() => {
                  selectPage(
                    currentPage < pageCount ? currentPage + 1 : currentPage
                  );
                }}
                className="linkDark"
              >
                &gt;
              </Link>
            </p>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionnaireReport;
